import moment, { Moment } from "moment";

export interface BondOrderHistoryResponse {
  uuid: string;
  cashflows: Cashflow[];
  bond_type: string;
  order_status: string;
  deal_type: string;
  bid_offer: string;
  isin: string;
  rating: string;
  rating_agency: string;
  value: string;
  minimum_order_value: string;
  type_of_yield: string;
  sell_yield: string;
  sell_price: string;
  settlement_type: string;
  gfd: string;
  deal_time_hours: string;
  deal_time_minutes: string;
  otootm: string;
  oto_participant_name: string;
  pro_client: string;
  buyer_client_name: string;
  direct_brokered: string;
  seller_broker_name: string;
  buyer_broker_name: string;
  negotiable_flag: string;
  disclosed_identity: string;
  initiator_custodian: string;
  initiator_reference_number: string;
  initiator_comment: string;
  extra_data: any;
  is_added_on_rfq: boolean;
  rfq_order_number: string;
  rfq_deal_id: string;
  no_of_bonds: number;
  source?: string;
  principal_amount: string;
  modified_accrued_interest: string;
  stamp_duty?: number | string | null;
  total_consideration: string;
  buyer_full_name: string;
  seller_name: string;
  initiator_ifsc: string;
  initiator_bank_account_number: string;
  initiator_dp_type: string;
  initiator_dpid: string;
  initiator_client_id: string;
  initiator_name: string;
  responder_ifsc: string;
  responder_bank_account_number: string;
  responder_dp_type: string;
  responder_dpid: string;
  responder_client_id: string;
  fund_credited: string;
  fund_debited: string;
  security_credited: string;
  security_debited: string;
  dealstatus_settled: string;
  payment_process: string;
  security_name: string;
  settlement_date: string;
  deal_status: string;
  isfundtransfer_Uploaded: boolean;
  deal_sheet_name: string;
  order_sheet_name: string;
  deal_confirmation_filename: string;
  rfq_order_external_id: string;
  fundtransfer_image_name: any;
  payment_link_order_number: number;
  payment_link: string;
  razorpay_order_id: string;
  razorpay_fe_order_id: string;
  razorpay_transfer_order_id: string;
  payment_status: string;
  payment_link_generated_at: string;
  issuer_master_image_s3_url: string;
  last_maturity_date: string;
  last_call_date: string;
  payment_cancelled: boolean;
  payment_cancelled_at: any;
  offer_value: string;
  offerminvalue: string;
  offeryieldtype: string;
  offeryield: string;
  offerprice: string;
  sellerclientcode: string;
  quotetype: string;
  dirtyprice: string;
  insertedAt: string;
  updatedAt: string;
  inserted_at?: string;
  updated_at?: string;
  user: string;
  security_type: string;
  tax_category: string;
  frequency_of_interest: string;
  put_options: PutOption[];
  order_deleted_date?: string | null;
  is_delete?: boolean;
}

export interface Cashflow {
  uuid: string;
  date: string;
  amount: string;
  type: string;
  inserted_at: string;
  updated_at: string;
  rfq_order: string;
}

export interface PutOption {
  date: string;
  amount: string;
}

export const isExternalBondOrder = (order: BondOrderHistoryResponse) =>
  !order.deal_status;

export type ExternalTagLabel = "EXTERNAL" | "IPO";

const IPO_EXTERNAL_SOURCE_VALUES = new Set(["SMEST", "SMEST IPO"]);

export const getOrderSource = (order: BondOrderHistoryResponse) =>
  `${order.source || ""}`.trim();

export const isSmestExternalBondOrder = (order: BondOrderHistoryResponse) =>
  isExternalBondOrder(order) &&
  IPO_EXTERNAL_SOURCE_VALUES.has(getOrderSource(order).toUpperCase());

export const usesExternalDeleteApi = (order: BondOrderHistoryResponse) =>
  isExternalBondOrder(order);

export const getExternalTagLabel = (
  order: BondOrderHistoryResponse
): ExternalTagLabel | null => {
  if (!isExternalBondOrder(order)) return null;
  return isSmestExternalBondOrder(order) ? "IPO" : "EXTERNAL";
};

export const isInternalBondOrder = (order: BondOrderHistoryResponse) =>
  !usesExternalDeleteApi(order);

export const isInternalOrderDeleted = (order: BondOrderHistoryResponse) =>
  isInternalBondOrder(order) &&
  Boolean(order.is_delete) &&
  Boolean(order.order_deleted_date);

export const getOrderInvestmentAmount = (
  order: Pick<BondOrderHistoryResponse, "total_consideration"> & {
    stamp_duty?: number | string | null;
  }
) => {
  const total = Number(order.total_consideration) || 0;
  const stampDuty = Number(order.stamp_duty ?? 0) || 0;
  return total + stampDuty;
};

export interface CalendarCashflowValues {
  amount: number;
  type: string;
  uuid: string;
  isin: string;
  securityType: string;
  taxCategory: string;
  noOfBonds: number;
  securityName: string;
  frequency: string;
  maturityOrCallDate: string;
  maturityOrCallDateType: string;
  coupon: string;
  issuerName: string;
}

export interface HeatmapData {
  year: number;
  month: string;
  numberOfDaysInMonth: number;
  datesOfMonth: {
    date: Moment;
    cashflows: CalendarCashflowValues[];
  }[];
}

export interface IPOOrderHistoryResponse {
  uuid: string;
  scripid: string;
  applicationno: string;
  category: string;
  applicantname: string;
  depository: string;
  dpid: string;
  clientbenfid: string;
  user_id: string;
  chequereceivedflag: string;
  chequeamount: string;
  panno: string;
  bankname: string;
  location: string;
  accountnumber_upiid: string;
  ifsccode: string;
  chequeno: string;
  bankreferenceno: string;
  bidtype: string;
  scripcode: string;
  dpflag: string;
  dpremark: string;
  modeofpayment: string;
  membertype: string;
  errorcode: string;
  message: string;
  sponsorbankpaystatus: string;
  sponsorbankactiontime: string;
  sponsorbankamtblocked: string;
  rejectreason: string;
  bids: Bid[];
  allotmentstatus: string;
  ipo_quote_tenure: string;
  final_units: string;
  totalqty: string;
  bankreferencechequeno: string;
  insertedAt: Date;
  updatedAt: Date;
  image: string;
  issuer_name: string;
  ipo_security_name: string;
  smest_Id: string;
  mobile: string;
  email: string;
  issuer_of_security: string;
  order_placed: string;
  isin: string;
  is_processed: boolean;
}

export interface Bid {
  rate: string;
  bidid: string;
  series: string;
  orderno: string;
  quantity: string;
  cuttoffflag: string;
}

export const computeHeatmapData = (
  orders: BondOrderHistoryResponse[] | undefined
): HeatmapData[] => {
  if (!orders) return [];

  const settledOrders = orders.filter(
    (order) => order.deal_status === "Settled"
  );

  const getOrderCashflows = (order: BondOrderHistoryResponse) =>
    Array.isArray(order.cashflows) ? order.cashflows : [];

  const cashflowDates = settledOrders.flatMap((order) =>
    getOrderCashflows(order).map((cashflow) => moment(cashflow.date))
  );

  if (cashflowDates.length === 0) return [];

  const startDate = moment.min(cashflowDates).startOf("month");
  const endDate = moment.max(cashflowDates).endOf("month");
  const results: HeatmapData[] = [];

  let currentDate = startDate.clone();
  while (currentDate.isSameOrBefore(endDate, "month")) {
    const year = currentDate.year();
    const month = currentDate.format("MMM");
    const numberOfDaysInMonth = currentDate.daysInMonth();
    const datesOfMonth: {
      date: Moment;
      cashflows: CalendarCashflowValues[];
    }[] = [];

    const monthReference = currentDate.clone();
    for (let day = 1; day <= numberOfDaysInMonth; day++) {
      const date = monthReference.clone().date(day);
      const cashflows = settledOrders.flatMap((order) =>
        getOrderCashflows(order)
          .filter((cashflow) => moment(cashflow.date).isSame(date, "day"))
          .map((cashflow) => ({
            amount: Number(cashflow.amount),
            type: cashflow.type,
            uuid: order.uuid,
            isin: order.isin,
            securityType: order.security_type,
            taxCategory: order.tax_category,
            noOfBonds: order.no_of_bonds,
            securityName: order.security_name,
            frequency: order.frequency_of_interest,
            maturityOrCallDate: order.last_call_date
              ? order.last_call_date
              : order.put_options?.length &&
                moment(order.put_options[0].date, "YYYY-MM-DD").isSame(
                  moment(order.last_maturity_date, "DD/MM/YYYY"),
                  "day"
                )
              ? order.put_options[0].date
              : order.last_maturity_date,
            maturityOrCallDateType: order.last_call_date
              ? "Call Date"
              : order.put_options?.length &&
                moment(order.put_options[0].date, "YYYY-MM-DD").isSame(
                  moment(order.last_maturity_date, "DD/MM/YYYY"),
                  "day"
                )
              ? "Put Date"
              : "Maturity Date",
            coupon: order.security_name.split(" ")[0],
            issuerName: "NA",
          }))
      );

      datesOfMonth.push({ date, cashflows });
    }

    results.push({
      year,
      month,
      numberOfDaysInMonth,
      datesOfMonth,
    });

    currentDate.add(1, "month");
  }

  return results;
};

export const disclaimerPoints = [
  "a) The information provided above is an illustrative sample based on or derived from the Cash Flow outlined in the respective Information Memorandum. It is for informational purposes only and should not be relied upon or represented without verification against the bank statement or other original sources.",
  "b) The data should be cross-verified with appropriate document (i.e. the actual bank statement linked to your demat account holding the respective securities). In the event of any discrepancies, you are advised to contact the Issuer Company directly. Please note that variations in payment values or dates may arise due to holiday conventions or other adjustments made by the issuer, trustee, or Registrar and Transfer Agent (RTA), as outlined in the Information Memorandum or Disclosure Document.",
  "c) Tax Deducted at Source (TDS) may be applicable to interest payments on debt instruments, as per prevailing laws and regulation.",
  "d) The bond holdings reflected here may differ from those in your demat account, particularly if any bonds have been sold since the report was generated.",
];

export const calculateRowHeight = (
  text: string,
  columnWidth: number
): number => {
  const avgCharWidth = 1.1;
  const lineHeight = 18;
  const maxCharsPerLine = Math.floor((columnWidth * 0.9) / avgCharWidth);

  const words = text.split(" ");
  let lineCount = 1;
  let currentLineLength = 0;

  words.forEach((word) => {
    const wordLength = word.length;

    if (wordLength > maxCharsPerLine) {
      lineCount += Math.ceil(wordLength / maxCharsPerLine);
      currentLineLength = wordLength % maxCharsPerLine;
    } else if (currentLineLength + wordLength > maxCharsPerLine) {
      lineCount++;
      currentLineLength = wordLength;
    } else {
      currentLineLength += wordLength + 1;
    }
  });

  return Math.max(lineCount * lineHeight * 1.1, 40);
};

export const getBondOrderSide = (bidOffer?: string) => {
  const normalized = bidOffer
    ? bidOffer.replace(/\s+/g, " ").trim().toUpperCase()
    : "";

  if (normalized === "SELL" || normalized === "OFFER") {
    return "Sell";
  }

  if (normalized === "BUY" || normalized === "BID") {
    return "Buy";
  }

  return normalized || "Buy";
};

export const BOND_ORDER_HISTORY_ENDPOINTS = [
  (uuid: string) => `order_management_v2/orders/history/${uuid}/`,
  (uuid: string) => `order_management_v2/client/order/history/${uuid}/`,
];

export const USER_TYPE_BOND_ORDER_HISTORY_ENDPOINTS = [
  (uuid: string, user_type: string) => `order_management_v2/orders/history/${uuid}/?type=${user_type}`,
  (uuid: string, user_type: string) => `order_management_v2/client/order/history/${uuid}/?type=${user_type}`,
];

export const normalizeBondOrdersPayload = (
  payload: unknown
): BondOrderHistoryResponse[] => {
  if (Array.isArray(payload)) {
    return payload as BondOrderHistoryResponse[];
  }

  if (payload && typeof payload === "object" && "data" in payload) {
    const nested = (payload as { data?: unknown }).data;
    if (Array.isArray(nested)) {
      return nested as BondOrderHistoryResponse[];
    }
  }

  return [];
};

export const dedupeBondOrders = (
  orders: BondOrderHistoryResponse[]
): BondOrderHistoryResponse[] => {
  const seen = new Set<string>();

  return orders.filter((order) => {
    if (!order?.uuid) {
      return true;
    }

    if (seen.has(order.uuid)) {
      return false;
    }

    seen.add(order.uuid);
    return true;
  });
};
